import { parseXmlDocument } from "./parseTinglysningsDataXml";
import type { ServitutDokumentAnmelder } from "./parseServitutDokumentXml";

type XmlEl = ReturnType<typeof parseXmlDocument>;

export type PaategningFilingKind = "servitut" | "paategning" | "matrikulaerAendring" | "andet";

export type ServitutPaategningFiling = {
   id: string;
   tidspunkt: string;
   ekspeditionstype: string;
   kind: PaategningFilingKind;
   anmelder: ServitutDokumentAnmelder | null;
   bilagRefs: string[];
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function directChildren(el: XmlEl | undefined, tag: string): XmlEl[] {
   return el?.children.filter((child) => child.tag === tag) ?? [];
}

function firstDirect(el: XmlEl | undefined, tag: string): XmlEl | undefined {
   return directChildren(el, tag)[0];
}

function firstDescendant(el: XmlEl | undefined, tag: string): XmlEl | undefined {
   if (!el) {
      return undefined;
   }
   if (el.tag === tag) {
      return el;
   }
   for (const child of el.children) {
      const found = firstDescendant(child, tag);
      if (found) {
         return found;
      }
   }
   return undefined;
}

function collectByTag(el: XmlEl | undefined, tag: string): XmlEl[] {
   if (!el) {
      return [];
   }
   const found: XmlEl[] = [];
   const walk = (node: XmlEl) => {
      if (node.tag === tag) {
         found.push(node);
      }
      node.children.forEach(walk);
   };
   walk(el);
   return found;
}

function textOf(el: XmlEl | undefined, tag?: string): string {
   const target = tag ? firstDescendant(el, tag) : el;
   return (target?.text ?? "").trim();
}

function xmlInputToText(input: Document | string | null | undefined): string {
   if (!input) {
      return "";
   }
   if (typeof input === "string") {
      return input;
   }
   if (typeof XMLSerializer !== "undefined") {
      return new XMLSerializer().serializeToString(input);
   }
   return input.documentElement?.outerHTML || "";
}

function filingKindFromAnmeldelse(tingbog: XmlEl | undefined, ekspeditionstype: string): PaategningFilingKind {
   const filingTag = tingbog?.children[0]?.tag || "";
   if (filingTag === "ServitutKombinationPaategn" || ekspeditionstype === "106") {
      return "paategning";
   }
   if (filingTag === "ServitutOpret" || ekspeditionstype === "50") {
      return "servitut";
   }
   if (filingTag === "EjendomOpret" || ekspeditionstype === "185") {
      return "matrikulaerAendring";
   }
   return "andet";
}

function parseSnapshotNames(root: XmlEl): Map<string, string> {
   const names = new Map<string, string>();
   const snapshot = firstDescendant(root, "SnapshotSamling");
   if (!snapshot) {
      return names;
   }
   const walk = (node: XmlEl) => {
      if (node.tag === "RolleStruktur") {
         const name = textOf(firstDirect(node, "Navn")) || textOf(node, "LegalUnitName");
         const cvr = textOf(node, "CVRnumberIdentifier");
         if (name && cvr) {
            names.set(`cvr:${cvr}`, name);
         }
         return;
      }
      node.children.forEach(walk);
   };
   walk(snapshot);
   return names;
}

function parseAnmelder(svar: XmlEl): ServitutDokumentAnmelder | null {
   const info = firstDescendant(svar, "AnmelderInformation");
   if (!info) {
      return null;
   }
   const names = parseSnapshotNames(svar);
   const cvr = textOf(info, "CVRnumberIdentifier");
   const anmelder: ServitutDokumentAnmelder = {
      cvr,
      name: (cvr && names.get(`cvr:${cvr}`)) || textOf(info, "LegalUnitName") || textOf(info, "PersonName"),
      phone: textOf(info, "TelephoneNumberIdentifier"),
      email: textOf(info, "EmailAddressIdentifier"),
   };
   if (!anmelder.cvr && !anmelder.name) {
      return null;
   }
   return anmelder;
}

function parseBilagRefs(svar: XmlEl): string[] {
   const refs: string[] = [];
   const seen = new Set<string>();
   for (const el of collectByTag(firstDescendant(svar, "AnmeldelseInformation") ?? svar, "BilagReference")) {
      const value = el.text.trim();
      if (!UUID_RE.test(value) || seen.has(value.toLowerCase())) {
         continue;
      }
      seen.add(value.toLowerCase());
      refs.push(value);
   }
   return refs;
}

function parseFiling(svar: XmlEl): ServitutPaategningFiling | null {
   const id = textOf(firstDirect(svar, "AnmeldelseIdentifikator"));
   const tidspunkt = textOf(firstDirect(svar, "AnmeldelsesTidspunkt"));
   if (!id && !tidspunkt) {
      return null;
   }
   const tingbog = firstDirect(svar, "AnmeldelseTingbog");
   const stamp = firstDescendant(svar, "TinglysningspaategningElement");
   const ekspeditionstype = textOf(firstDirect(stamp, "EkspeditionstypeIdentifikator"))
      || textOf(tingbog, "EkspeditionstypeIdentifikator");
   return {
      id,
      tidspunkt,
      ekspeditionstype,
      kind: filingKindFromAnmeldelse(tingbog, ekspeditionstype),
      anmelder: parseAnmelder(svar),
      bilagRefs: parseBilagRefs(svar),
   };
}

export function parseDokumentRevisionssporXml(input: Document | string | null | undefined): ServitutPaategningFiling[] {
   const xml = xmlInputToText(input).trim();
   if (!xml) {
      return [];
   }
   try {
      const root = parseXmlDocument(xml);
      return collectByTag(root, "AnmeldelseSvar")
         .filter((svar) => firstDirect(svar, "AnmeldelseTingbog"))
         .map(parseFiling)
         .filter((filing): filing is ServitutPaategningFiling => Boolean(filing));
   } catch {
      return [];
   }
}

export function formatPaategningFilingType(kind: PaategningFilingKind): string {
   if (kind === "paategning") {
      return "Endorsement";
   }
   if (kind === "servitut") {
      return "Servitut";
   }
   if (kind === "matrikulaerAendring") {
      return "Cadastral change";
   }
   return "";
}
